import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  addAddress,
  updateAddress,
  clearCurrentAddress,
  clearAddressState,
} from "../../../Redux/customer-address/slice";
import { CreateAddressRequest } from "../../../Redux/customer-address/types";
import {
  fetchAllCountries,
  fetchStatesByCountryName,
  fetchDistrictsByStateName,
} from "../../../Redux/location-mangment/slice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { toast } from "react-toastify";
import { Check, Save, ShieldCheck, X } from "lucide-react";
import LocationPicker, {
  ResolvedAddress,
} from "../../../ecommerce/globalcomponents/LocationPicker";
import AddressSelect from "./AddressSelect";

interface AddNewAddressProps {
  onSuccess?: () => void;
  onClose?: () => void;
  initialValues?: Partial<CreateAddressRequest>;
}

const AddNewAddress: React.FC<AddNewAddressProps> = ({
  onSuccess,
  onClose,
  initialValues,
}) => {
  const dispatch = useAppDispatch();
  const locDispatch = useDispatch<AppDispatch>();

  const { currentAddress, loading, success, message, error } = useAppSelector(
    (state) => state.customerAddress,
  );

  const {
    countries,
    states,
    districts,
    loading: locLoading,
  } = useSelector((state: RootState) => state.location);

  const { addresses } = useAppSelector((state) => state.customerAddress);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateAddressRequest>({
    fullName: "",
    email: "",
    phoneNumber: "",
    country: "India",
    state: "",
    district: "",
    city: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    pincode: "",
    addressType: "Home",
    defaultAddress: false,
    latitude: undefined,
    longitude: undefined,
    ...initialValues,
  });

  // ── Fetch countries on mount ──
  useEffect(() => {
    locDispatch(fetchAllCountries());
    // Ensure the dropdown options include any prefilled values.
    if (initialValues?.country)
      locDispatch(fetchStatesByCountryName(initialValues.country));
    if (initialValues?.state)
      locDispatch(fetchDistrictsByStateName(initialValues.state));
  }, [locDispatch]);

  // ── Populate form for edit ──
  useEffect(() => {
    if (currentAddress) {
      setFormData({
        fullName: currentAddress.fullName || "",
        email: currentAddress.email || "",
        phoneNumber: currentAddress.phoneNumber || "",
        country: currentAddress.country || "India",
        state: currentAddress.state || "",
        district: currentAddress.district || "",
        city: currentAddress.city || "",
        addressLine1: currentAddress.addressLine1 || "",
        addressLine2: currentAddress.addressLine2 || "",
        landmark: currentAddress.landmark || "",
        pincode: currentAddress.pincode || "",
        addressType: currentAddress.addressType || "Home",
        defaultAddress: currentAddress.defaultAddress || false,
        latitude: currentAddress.latitude,
        longitude: currentAddress.longitude,
      });
      if (currentAddress.country)
        locDispatch(fetchStatesByCountryName(currentAddress.country));
      if (currentAddress.state)
        locDispatch(fetchDistrictsByStateName(currentAddress.state));
    } else {
      resetForm();
    }
  }, [currentAddress]);

  // ── Pre-fetch states for default "India" ──
  useEffect(() => {
    if (states.length === 0 && !locLoading) locDispatch(fetchStatesByCountryName("India"));
  }, []);

  // ── Toasts ── (also reset local submitting flag here)
  useEffect(() => {
    if (success && message) {
      toast.success(message, { autoClose: 3000, toastId: "address-success" });
      setIsSubmitting(false);
      if (!currentAddress) resetForm();
      dispatch(clearAddressState());
      if (onSuccess) onSuccess();
    }
  }, [success, message]);

  useEffect(() => {
    if (error) {
      toast.error(error, { autoClose: 3000, toastId: "address-error" });
      setIsSubmitting(false);
    }
  }, [error]);

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phoneNumber: "",
      country: "India",
      state: "",
      district: "",
      city: "",
      addressLine1: "",
      addressLine2: "",
      landmark: "",
      pincode: "",
      addressType: "Home",
      defaultAddress: false,
      latitude: undefined,
      longitude: undefined,
      ...initialValues,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    let val =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    // Real-time validation
    if (typeof val === "string") {
      if (name === "fullName") {
        val = val
          .replace(/[^a-zA-Z\s]/g, "")
          .replace(/^\s+/, "")
          .replace(/\s{2,}/g, " ")
          .slice(0, 40);
      } else if (name === "email") {
        // Only "@" and "." as special chars allowed
        val = val.replace(/\s/g, "").replace(/[^a-zA-Z0-9@.]/g, "");
        // Enforce single "@"
        const firstAt = val.indexOf("@");
        if (firstAt !== -1) {
          val = val.slice(0, firstAt + 1) + val.slice(firstAt + 1).replace(/@/g, "");
        }
        // Enforce max 2 dots
        let dotCount = 0;
        val = val.replace(/\./g, (d) => dotCount++ < 2 ? d : "");
        // Enforce max 6 digits — strip extras
        let digitCount = 0;
        val = val.replace(/\d/g, (d) => digitCount++ < 6 ? d : "");
        // Max 30 chars
        if (val.length > 30) val = val.slice(0, 30);
      } else if (name === "city") {
        if (/[^a-zA-Z\s]/.test(val)) {
          toast.error("City must contain only alphabets", {
            autoClose: 3000,
          });
        }
        val = val
          .replace(/[^a-zA-Z\s]/g, "")
          .replace(/^\s+/, "")
          .replace(/\s{2,}/g, " ")
          .slice(0, 25);
      } else if (name === "phoneNumber") {
        val = val.replace(/\D/g, "").slice(0, 10);
      } else if (name === "addressLine1" || name === "addressLine2") {
        val = val.replace(/[^a-zA-Z0-9\s\-,\/_]/g, "").slice(0, 100);
      } else if (name === "landmark") {
        val = val.replace(/[^a-zA-Z0-9\s]/g, "").replace(/^\s+/, "").replace(/\s{2,}/g, " ");
        // Strip digits beyond 2
        let digitCount = 0;
        val = val.replace(/\d/g, (d) => digitCount++ < 2 ? d : "");
        val = val.slice(0, 30);
      } else if (name === "pincode") {
        val = val.replace(/\D/g, "").slice(0, 6);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleCountrySelect = (countryName: string) => {
    setFormData((prev) => ({
      ...prev,
      country: countryName,
      state: "",
      district: "",
    }));
    locDispatch(fetchStatesByCountryName(countryName));
  };

  const handleStateSelect = (stateName: string) => {
    setFormData((prev) => ({ ...prev, state: stateName, district: "" }));
    locDispatch(fetchDistrictsByStateName(stateName));
  };

  const handleDistrictSelect = (districtName: string) => {
    setFormData((prev) => ({ ...prev, district: districtName }));
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
  };

  // Auto-fill address fields from the pinned point (reverse geocoded).
  const handleAddressResolve = (addr: ResolvedAddress) => {
    setFormData((prev) => ({
      ...prev,
      country: addr.country || prev.country,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      district: addr.district || prev.district,
      pincode: addr.pincode || prev.pincode,
      addressLine1: prev.addressLine1 || addr.addressLine || "",
    }));
    if (addr.country) locDispatch(fetchStatesByCountryName(addr.country));
    if (addr.state) locDispatch(fetchDistrictsByStateName(addr.state));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any stale field errors at submission time
    setErrors({});

    // Email validation: exactly one "@" and 1-2 dots in domain
    const atCount = (formData.email.match(/@/g) || []).length;
    if (atCount !== 1) {
      toast.error("Email must contain exactly one '@' character.");
      return;
    }
    const [, domainPart] = formData.email.split("@");
    const domainDotCount = (domainPart?.match(/\./g) || []).length;
    if (domainDotCount < 1 || domainDotCount > 2) {
      toast.error("Email domain must have 1 or 2 dots (e.g. .com or .co.in).");
      return;
    }

    // Mobile Number validation: must be exactly 10 digits
    if (formData.phoneNumber.length !== 10) {
      toast.error("Mobile number must be exactly 10 digits.");
      return;
    }

    // Pincode validation: must be 6 digits and not "000000"
    if (formData.pincode.length !== 6) {
      toast.error("Pincode must be exactly 6 digits.");
      return;
    }
    if (formData.pincode === "000000") {
      toast.error("Invalid pincode (000000).");
      return;
    }

    // Duplicate Check: Exclude the current address being edited
    const isDuplicate = addresses
      .filter((addr) => addr._id !== currentAddress?._id)
      .some(
        (addr) =>
          addr.fullName.trim().toLowerCase() ===
            formData.fullName.trim().toLowerCase() &&
          addr.addressLine1.trim().toLowerCase() ===
            formData.addressLine1.trim().toLowerCase() &&
          addr.city.trim().toLowerCase() ===
            formData.city.trim().toLowerCase() &&
          addr.pincode.trim() === formData.pincode.trim(),
      );

    if (isDuplicate) {
      toast.error("This address already exists.");
      return;
    }

    // State and district are custom dropdowns — browser required won't fire for them
    if (!formData.state) {
      toast.error("Please select a state.");
      return;
    }
    if (!formData.district) {
      toast.error("Please select a district.");
      return;
    }
    if (formData.latitude == null || formData.longitude == null) {
      toast.error("Please pin your exact location on the map.");
      return;
    }

    // Drop empty optional fields — backend @IsOptional skips undefined/null but
    // still validates empty strings, which fails the addressLine2/landmark rules.
    const payload: CreateAddressRequest = { ...formData };
    if (!payload.addressLine2?.trim()) delete payload.addressLine2;
    if (!payload.landmark?.trim()) delete payload.landmark;

    setIsSubmitting(true);
    if (currentAddress && currentAddress._id) {
      dispatch(updateAddress({ id: currentAddress._id, data: payload }));
    } else {
      dispatch(addAddress(payload));
    }
  };

  const handleCancelEdit = () => {
    dispatch(clearCurrentAddress());
    resetForm();
    if (onClose) onClose();
  };

  const countryOptions = countries.map((c) => c.countryName).sort((a, b) => a.localeCompare(b));
  const stateOptions = states.map((s) => s.stateName).sort((a, b) => a.localeCompare(b));
  const districtOptions = districts.map((d) => d.districtName).sort((a, b) => a.localeCompare(b));

  return (
    <form className="manage-address-form" onSubmit={handleSubmit}>
      <div className="manage-address-form-heading">
        <div>
          <span>{currentAddress ? "EDIT ADDRESS" : "NEW ADDRESS"}</span>
          <h2>{currentAddress ? "Update Address" : "Add Address"}</h2>
        </div>

        <button
          type="button"
          className="manage-address-form-close"
          onClick={handleCancelEdit}
          aria-label="Close address form"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>

      <div className="manage-address-form-grid">
        <div className="manage-address-field manage-address-field-full">
          <label htmlFor="manage-address-full-name">Full Name</label>
          <input
            id="manage-address-full-name"
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter Full Name"
            required
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-email">E-mail ID</label>
          <input
            id="manage-address-email"
            type="text"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            placeholder="Enter E-mail ID"
            required
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-mobile">Mobile Number</label>
          <input
            id="manage-address-mobile"
            type="tel"
            inputMode="numeric"
            name="phoneNumber"
            maxLength={10}
            value={formData.phoneNumber}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            placeholder="Enter Mobile Number"
            required
          />
        </div>

        <div className="manage-address-field">
          <AddressSelect
            id="manage-address-country"
            label="Country"
            value={formData.country}
            options={countryOptions}
            placeholder="Select Country"
            loading={locLoading && countries.length === 0}
            disabled={!!currentAddress}
            onSelect={handleCountrySelect}
          />
        </div>

        <div className="manage-address-field">
          <AddressSelect
            id="manage-address-state"
            label="State"
            value={formData.state}
            options={stateOptions}
            placeholder={formData.country ? "Select State" : "Select Country First"}
            loading={locLoading && !!formData.country && states.length === 0}
            disabled={!formData.country}
            onSelect={handleStateSelect}
          />
        </div>

        <div className="manage-address-field">
          <AddressSelect
            id="manage-address-district"
            label="District"
            value={formData.district}
            options={districtOptions}
            placeholder={formData.state ? "Select District" : "Select State First"}
            loading={locLoading && !!formData.state && districts.length === 0}
            disabled={!formData.state}
            onSelect={handleDistrictSelect}
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-city">City</label>
          <input
            id="manage-address-city"
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="Enter City"
            required
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-line-1">Address Line 1</label>
          <input
            id="manage-address-line-1"
            type="text"
            name="addressLine1"
            value={formData.addressLine1}
            onChange={handleChange}
            placeholder="House / Flat / Building"
            required
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-line-2">Address Line 2</label>
          <input
            id="manage-address-line-2"
            type="text"
            name="addressLine2"
            value={formData.addressLine2}
            onChange={handleChange}
            placeholder="Street / Area"
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-landmark">Landmark</label>
          <input
            id="manage-address-landmark"
            type="text"
            name="landmark"
            value={formData.landmark}
            onChange={handleChange}
            placeholder="Landmark (optional)"
          />
        </div>

        <div className="manage-address-field">
          <label htmlFor="manage-address-pincode">Pincode</label>
          <input
            id="manage-address-pincode"
            type="text"
            inputMode="numeric"
            name="pincode"
            maxLength={6}
            value={formData.pincode}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            placeholder="Enter Pincode"
            required
          />
        </div>

        <div className="manage-address-field manage-address-field-full">
          <label>Address Type</label>
          <div className="manage-address-radio-group">
            {["Home", "Office", "Other"].map((type) => (
              <label
                key={type}
                className={`manage-address-radio ${formData.addressType === type ? "active" : ""}`}
              >
                <input
                  type="radio"
                  name="addressType"
                  value={type}
                  checked={formData.addressType === type}
                  onChange={handleChange}
                />
                {type}
              </label>
            ))}
          </div>
        </div>

        <label className="manage-address-default-checkbox manage-address-field-full">
          <input
            type="checkbox"
            name="defaultAddress"
            checked={formData.defaultAddress}
            onChange={handleChange}
          />
          <span className="manage-address-checkmark">
            {formData.defaultAddress && <Check size={12} strokeWidth={3} />}
          </span>
          <span>Set as default address</span>
        </label>

        <div className="manage-address-map-field manage-address-field-full">
          <label>Pin Your Location</label>
          <LocationPicker
            latitude={formData.latitude}
            longitude={formData.longitude}
            onChange={handleLocationChange}
            onAddressResolve={handleAddressResolve}
          />
        </div>
      </div>

      <div className="manage-address-form-actions">
        <div className="manage-address-secure-note">
          <ShieldCheck size={14} strokeWidth={2} />
          <span>Your address is securely stored for delivery.</span>
        </div>

        <div className="manage-address-action-buttons">
          <button
            type="button"
            className="manage-address-cancel-button"
            onClick={handleCancelEdit}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="manage-address-save-button"
            disabled={isSubmitting || loading}
          >
            <Save size={15} strokeWidth={2} />
            {isSubmitting
              ? "Saving..."
              : currentAddress
                ? "Update Address"
                : "Save Address"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddNewAddress;
